import React, { useState, useEffect } from 'react';
import { useHashPackWallet } from '../contexts/HashPackWalletContext';
import { ContractService } from '../services/contractService';
import { hashPackWallet } from '../services/wallets/hashpack/hashpackWallet';
import { getSaucerSwapAddress } from '../config/contracts';

interface SwapFormData {
  tokenIn: string;
  tokenOut: string;
  amountIn: string;
  amountOutMinimum: string;
  slippage: number;
  fee: number;
}

export const ProfitableSwap: React.FC = () => {
  const { isConnected, accountId } = useHashPackWallet();

  const [swapData, setSwapData] = useState<SwapFormData>({
    tokenIn: '',
    tokenOut: '',
    amountIn: '',
    amountOutMinimum: '',
    slippage: 1, // 1%
    fee: 500, // 0.05%
  });

  const [loading, setLoading] = useState(false);
  const [userStats, setUserStats] = useState<any>(null);
  const [swapHistory, setSwapHistory] = useState<any[]>([]);

  const contractService = accountId ? new ContractService(hashPackWallet) : null;
  const connectedAddress = accountId;

  useEffect(() => {
    // if (contractService && connectedAddress) {
    //   fetchUserStats();
    // }
  }, [contractService, connectedAddress]);

  const fetchUserStats = async () => {
    if (!contractService || !connectedAddress) return;

    try {
      const stats = await contractService.getUserStats(connectedAddress);
      setUserStats(stats);

      const historyLength = await contractService.getUserSwapHistoryLength(connectedAddress);
      // Note: You'd need to implement getUserSwapRecord calls to get full history
      console.log('Swap history length:', historyLength);
    } catch (error) {
      console.error('Error fetching user stats:', error);
    }
  };

  const handleInputChange = (field: keyof SwapFormData, value: string | number) => {
    setSwapData(prev => ({ ...prev, [field]: value }));
  };

  const calculateDeadline = () => {
    return Math.floor(Date.now() / 1000) + 600; // 10 minutes from now
  };

  const calculateMinimumOutput = (amountIn: string, slippage: number) => {
    if (!amountIn) return '0';
    const amount = parseFloat(amountIn);
    const minOutput = amount * (1 - slippage / 100);
    return (minOutput * 1e18).toString(); // Convert to wei
  };

  const handleSwapHBARForTokens = async () => {
    if (!contractService || loading) return; // Prevent multiple simultaneous requests

    setLoading(true);
    try {
      const deadline = calculateDeadline();
      const amountOutMin = calculateMinimumOutput(swapData.amountIn, swapData.slippage);
      const hbarAmount = parseFloat(swapData.amountIn);

      const result = await contractService.swapExactHBARForTokens(
        swapData.tokenOut,
        swapData.fee,
        amountOutMin,
        deadline,
        hbarAmount
      );

      console.log('Swap HBAR for tokens result:', result);
      // await fetchUserStats(); // Refresh stats - disabled to prevent multiple requests
    } catch (error) {
      console.error('Error swapping HBAR for tokens:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSwapTokensForHBAR = async () => {
    if (!contractService) return;

    setLoading(true);
    try {
      const deadline = calculateDeadline();
      const amountIn = (parseFloat(swapData.amountIn) * 1e18).toString(); // Convert to wei
      const amountOutMin = calculateMinimumOutput(swapData.amountIn, swapData.slippage);

      const result = await contractService.swapExactTokensForHBAR(
        swapData.tokenIn,
        swapData.fee,
        amountIn,
        amountOutMin,
        deadline
      );

      console.log('Swap tokens for HBAR result:', result);
      // await fetchUserStats(); // Refresh stats - disabled to prevent multiple requests
    } catch (error) {
      console.error('Error swapping tokens for HBAR:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSwapTokensForTokens = async () => {
    if (!contractService) return;

    setLoading(true);
    try {
      const deadline = calculateDeadline();
      const amountIn = (parseFloat(swapData.amountIn) * 1e18).toString(); // Convert to wei
      const amountOutMin = calculateMinimumOutput(swapData.amountIn, swapData.slippage);

      const result = await contractService.swapExactTokensForTokens(
        swapData.tokenIn,
        swapData.tokenOut,
        swapData.fee,
        amountIn,
        amountOutMin,
        deadline
      );

      console.log('Swap tokens for tokens result:', result);
      // await fetchUserStats(); // Refresh stats - disabled to prevent multiple requests
    } catch (error) {
      console.error('Error swapping tokens for tokens:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isConnected) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-4">Profitable Swap</h2>
        <p className="text-gray-600">Please connect your wallet to use the swap functionality.</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-bold mb-6">Profitable Swap with Lottery</h2>

      {/* User Stats */}
      {userStats && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Your Stats</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Total Profits</p>
              <p className="text-lg font-bold">{userStats.totalProfits || '0'} HBAR</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Lottery Contributions</p>
              <p className="text-lg font-bold">{userStats.lotteryContributions || '0'} HBAR</p>
            </div>
          </div>
        </div>
      )}

      {/* Swap Form */}
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Token In</label>
            <input
              type="text"
              placeholder="Token address or 'HBAR'"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={swapData.tokenIn}
              onChange={(e) => handleInputChange('tokenIn', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Token Out</label>
            <input
              type="text"
              placeholder="Token address or 'HBAR'"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={swapData.tokenOut}
              onChange={(e) => handleInputChange('tokenOut', e.target.value)}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Amount In</label>
          <input
            type="number"
            placeholder="0.0"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            value={swapData.amountIn}
            onChange={(e) => handleInputChange('amountIn', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Slippage (%)</label>
            <input
              type="number"
              step="0.1"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={swapData.slippage}
              onChange={(e) => handleInputChange('slippage', parseFloat(e.target.value))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Fee Tier</label>
            <select
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={swapData.fee}
              onChange={(e) => handleInputChange('fee', parseInt(e.target.value))}
            >
              <option value={500}>0.05% (500)</option>
              <option value={3000}>0.3% (3000)</option>
              <option value={10000}>1% (10000)</option>
            </select>
          </div>
        </div>

        {/* Swap Buttons */}
        <div className="space-y-2">
          <button
            onClick={handleSwapHBARForTokens}
            disabled={loading || !swapData.tokenOut || !swapData.amountIn || swapData.tokenIn.toLowerCase() !== 'hbar'}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Swapping...' : 'Swap HBAR for Tokens'}
          </button>

          <button
            onClick={handleSwapTokensForHBAR}
            disabled={loading || !swapData.tokenIn || !swapData.amountIn || swapData.tokenOut.toLowerCase() !== 'hbar'}
            className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Swapping...' : 'Swap Tokens for HBAR'}
          </button>

          <button
            onClick={handleSwapTokensForTokens}
            disabled={loading || !swapData.tokenIn || !swapData.tokenOut || !swapData.amountIn ||
                     swapData.tokenIn.toLowerCase() === 'hbar' || swapData.tokenOut.toLowerCase() === 'hbar'}
            className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Swapping...' : 'Swap Tokens for Tokens'}
          </button>
        </div>
      </div>

      {/* Information */}
      <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
        <h4 className="font-semibold text-yellow-800 mb-2">💡 How it works:</h4>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• Make profitable round-trip swaps to earn rewards</li>
          <li>• 10% of profits are automatically charged</li>
          <li>• 8% goes to the lottery pool, 2% to platform</li>
          <li>• You're automatically entered in the lottery when making profits</li>
          <li>• Winners are selected using Pyth Entropy for fairness</li>
        </ul>
      </div>
    </div>
  );
};